import ScrollToTop from '@/features/marketing/components/ScrollToTop';
import Footer from '@/features/marketing/components/layout/Footer';
import Header from '@/features/marketing/components/layout/Header/Header';
import { HeaderContextProvider } from '@/shared/contexts/Header';
import { UserContextProvider } from '@/shared/contexts/User';
import { createTheme, ThemeOptions, ThemeProvider } from '@mui/material/styles';
import React, { ReactElement, ReactNode } from 'react';
import { ParallaxProvider } from 'react-scroll-parallax';
import { csCZ } from '@mui/x-date-pickers/locales';
import GeneralHandler from '@/shared/components/GeneralHandler';
import Toaster from '@/shared/ui/Toaster';
import { ModalContextProvider } from '@/shared/contexts/Modal';
import ModalManager from '@/shared/components/ModalManager';

type Props = {
    children : ReactNode;
    hasCustomMain ?: boolean;
};

export const MUITheme : ThemeOptions = {
    direction: 'ltr',
    palette: { primary: { main: '#5a2d91' }, warning: { main: '#ed6c02' } },
    components: {
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    padding: '0.75rem',
                    backgroundColor: '#424242',
                    color: '#FFFFFF',
                    fontSize: '0.9rem',
                },
            },
        },
        MuiButton: { styleOverrides: { startIcon: { '& svg': { fontSize: '1.25rem !important' } } } },
        MuiCardHeader: {
            styleOverrides: {
                title: {
                    color: '#5f7194',
                    fontWeight: 800,
                    fontSize: '1.125rem',
                    height: '24px',
                },
                avatar: {
                    'height': '24px',
                    'width': '24px',
                    'marginRight': '0.5rem',
                    '& svg': {
                        width: '24px',
                        height: '24px',
                    },
                },
            },
        },
        MuiCardActions: { styleOverrides: { root: { justifyContent: 'flex-end' } } },
        MuiTabs: {
            styleOverrides: {
                root: {
                    backgroundColor: '#ffffff', // Světle šedé pozadí
                    borderRadius: '4px', // Zakulacení rohů
                    padding: '2px',
                    boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.08)', // Jemný stín
                    border: '1px solid rgba(0, 0, 0, 0.12);',
                },
                indicator: {
                    backgroundColor: '#673AB7', // Zvýraznění aktivní záložky (fialová)
                    height: '3px', // Silnější podtržení
                    borderRadius: '3px 3px 0 0',
                },
            },
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    'fontWeight': 'bold',
                    'color': '#333', // Neaktivní záložky tmavě šedé
                    '&.Mui-selected': { color: '#673AB7' },
                    '&:hover': { backgroundColor: 'rgba(103, 58, 183, 0.05)' },
                },
            },
        },
        MuiTable: { styleOverrides: { root: { fontSize: '0.75rem' } } },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    fontSize: '0.75rem',
                    padding: '4px 8px',
                },
                head: { fontWeight: 600 },
            },
        },
    },
};

const General : React.FC<Props> = ({ children, hasCustomMain = false }) : ReactElement => {
    const theme = createTheme(MUITheme, csCZ);

    return (
        <div className='min-h-screen'>
            <ThemeProvider theme={theme}>
                <UserContextProvider>
                    {
                        <Toaster
                            position='top-center'
                            className='!w-fit'
                            theme='colored'
                            icon={false}
                        />
                    }
                    <ModalContextProvider>
                        <ModalManager />
                        <ParallaxProvider>
                            <ScrollToTop />
                            <HeaderContextProvider>
                                <Header />
                            </HeaderContextProvider>

                            <GeneralHandler hasCustomMain={hasCustomMain}>
                                {children}
                            </GeneralHandler>

                            <Footer />
                        </ParallaxProvider>
                    </ModalContextProvider>
                </UserContextProvider>
            </ThemeProvider>
        </div>
    );
};

export default General;
